/**
 * The OIDC session of the app and the console (PHASE-3 F1, 11 §2):
 * - Authorization Code + PKCE (oidc-client-ts), public client, no client secret;
 * - tokens in memory only (`InMemoryWebStorage`): a reload starts a top-level `prompt=none` round trip to
 *   Keycloak, whose SSO session signs the user in again without a form;
 * - refresh with the rotating refresh token (no iframe: the CSP has no frame-src for Keycloak). Keycloak revokes
 *   a refresh token on use (max reuse 0), so a tab never refreshes twice at once: one in-flight promise. Tabs
 *   keep their own tokens: each tab's code exchange starts its own refresh chain in Keycloak, and one tab's
 *   refresh does not invalidate another's (measured on the dev stack, F1 review M1). An OAuth error ends the
 *   session (except `temporarily_unavailable`, `server_error`); network errors are retried, and an expired token
 *   whose refresh keeps failing makes the session `unavailable` until a refresh works;
 * - one redirect at a time (`leaving`): sign-in, step-up, an action or the sign-out; while leaving nothing starts
 *   another, and a sign-out never turns into a new sign-in;
 * - step-up: a new authorization request with `acr_values=stepup` (COOP same-origin rules out popups);
 * - every `returnTo` passes `isSafeAppPath` (REQUESTS #6), anything else becomes "/".
 */
import { InMemoryWebStorage, UserManager, WebStorageStateStore, type User } from "oidc-client-ts";
import { isSafeAppPath } from "../routing";
import { oidcTransactionStorage, transientStore } from "./transient-store";
import { workspace } from "../workspace";
import { activeLocale } from "../i18n/locale";

export interface AuthConfig {
  /** `https://auth.<domain>/realms/customers` (or `platform`). */
  authority: string;
  clientId: string;
  /** The app's origin (`window.location.origin`). */
  origin: string;
  /** ACR the client asks for at login: `mfa` (customers) or `key` (platform). */
  loginAcr: string;
  /** ACR values a token may carry to count as signed in (default: `loginAcr`; customers also `stepup`). */
  acceptedAcr?: string[];
  /** Path of the redirect URI (registered in the realm as `<origin>/*`). */
  callbackPath?: string;
  /** Seconds before expiry when the access token is renewed (default 60). */
  renewBeforeSeconds?: number;
  /** Waits between refresh attempts after a network error (default 1 s, 4 s). */
  retryDelaysMs?: number[];
  /** A redirect that has not left the page after this long gives the page back (default 15 s). */
  leaveTimeoutMs?: number;
}

/** The API's step-up window (ADR-014: `auth_time` at most 10 minutes old; its challenge says `max_age="600"`). */
export const STEP_UP_MAX_AGE_SECONDS = 600;

/** Custom state round-tripped through the login (kept in the transient store, never in the URL). */
interface LoginState {
  returnTo: string;
  /** `login-mfa`: a second login because the first token was below the client's level (no third one). */
  kind: "login" | "login-mfa" | "stepup" | "action";
}

/**
 * `leaving`: a redirect to Keycloak is under way (sign-in, sign-out, step-up, an action); nothing may start another.
 * `unavailable`: the access token expired and Keycloak cannot be reached to refresh it (retrying).
 */
export interface CallbackResult {
  returnTo: string;
  interactive?: boolean;
  kind?: LoginState["kind"];
  signingOut?: boolean;
}

export type AuthStatus = "unknown" | "signed-in" | "signed-out" | "leaving" | "unavailable";

export interface SessionUser {
  sub: string;
  email?: string;
  name?: string;
  acr?: string;
  /** Seconds since epoch of the last authentication (step-up freshness is checked by the API). */
  authTime?: number;
  /** The language picked on Keycloak's pages (the token's `locale` claim, when the realm sends it; L1). */
  locale?: string;
}

export const safeReturnTo = (path: string | undefined | null): string => (path && isSafeAppPath(path) ? path : "/");

/** Keycloak answers a revoked, reused or expired refresh token with `invalid_grant`. */
export const isInvalidGrant = (e: unknown): boolean => (e as { error?: string } | null)?.error === "invalid_grant";

/**
 * An OAuth error from the token endpoint ends the session; `temporarily_unavailable` and `server_error` are
 * Keycloak having trouble and are retried like network errors (which carry no `error` code).
 */
export function isTerminalOAuthError(e: unknown): boolean {
  const code = (e as { error?: unknown } | null)?.error;
  return typeof code === "string" && code !== "" && code !== "temporarily_unavailable" && code !== "server_error";
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));
/** The renew timer never fires sooner than this (no tight loop when tokens live 60 s or less). */
const MIN_RENEW_DELAY_MS = 5_000;

type TabMessage = { type: "signed-out"; sub: string; reason: SignOutReason };

export type SignOutReason = "user" | "idle" | "everywhere";

/** Test seams for the browser APIs the session uses across tabs. */
export interface TabCoordination {
  /** Announces a sign-out to the other tabs (no tokens ever travel). */
  channel: Pick<BroadcastChannel, "postMessage" | "addEventListener" | "close"> | null;
  /** Top-level navigation (`window.location.assign`). */
  navigate: (url: string) => void;
}

function browserCoordination(name: string): TabCoordination {
  return {
    channel: typeof BroadcastChannel === "undefined" ? null : new BroadcastChannel(name),
    navigate: (url) => {
      window.location.assign(url);
    },
  };
}

export class AuthSession {
  readonly manager: UserManager;
  private readonly config: Required<AuthConfig>;
  private user: User | null = null;
  private current: Exclude<AuthStatus, "leaving"> = "unknown";
  private leaving = false;
  private leaveTimer: ReturnType<typeof setTimeout> | null = null;
  private stepUpReturn = false;
  /** Set once a sign-out started: the post-logout URL. Sticky: nothing ever signs this page in again. */
  private signingOut: string | null = null;
  private readonly disposers: (() => void)[] = [];
  private readonly listeners = new Set<() => void>();
  private inflight: Promise<User | null> | null = null;
  private renewTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly tabs: TabCoordination;

  constructor(config: AuthConfig, tabs?: TabCoordination) {
    this.config = {
      callbackPath: "/callback",
      acceptedAcr: [config.loginAcr],
      renewBeforeSeconds: 60,
      retryDelaysMs: [1_000, 4_000],
      leaveTimeoutMs: 15_000,
      ...config,
    };
    this.manager = new UserManager({
      authority: config.authority,
      client_id: config.clientId,
      redirect_uri: `${config.origin}${this.config.callbackPath}`,
      post_logout_redirect_uri: `${config.origin}/`,
      response_type: "code",
      scope: "openid profile email",
      userStore: new WebStorageStateStore({ store: new InMemoryWebStorage() }),
      stateStore: new WebStorageStateStore({ store: oidcTransactionStorage(), prefix: "" }),
      // Our own renew timer, through the one shared refresh (oidc-client-ts does not deduplicate).
      automaticSilentRenew: false,
      monitorSession: false,
      loadUserInfo: false,
      // Keep acr and auth_time in the profile (the level of the session; step-up freshness).
      filterProtocolClaims: ["nbf", "jti", "nonce", "at_hash", "c_hash", "s_hash"],
      revokeTokensOnSignout: true,
    });
    this.tabs = tabs ?? browserCoordination(`brevalt.session.${config.clientId}`);
    const onMessage = (e: Event) => {
      this.onTabMessage((e as MessageEvent<unknown>).data).catch(() => undefined);
    };
    this.tabs.channel?.addEventListener("message", onMessage);
    this.disposers.push(() => {
      this.tabs.channel?.close();
    });
    if (typeof window !== "undefined") {
      // Back/forward cache: a page restored after it left for Keycloak must not stay "leaving" (a page that was
      // signing out goes on to the signed-out page).
      const onPageShow = (e: PageTransitionEvent) => {
        if (e.persisted) this.resetLeaving();
      };
      // A tab that slept or was offline renews as soon as it can.
      const wake = () => {
        if (this.user && (this.user.expires_in ?? 0) <= this.config.renewBeforeSeconds) {
          this.renew().catch(() => undefined);
        }
      };
      const onVisible = () => {
        if (document.visibilityState === "visible") wake();
      };
      window.addEventListener("pageshow", onPageShow);
      window.addEventListener("online", wake);
      document.addEventListener("visibilitychange", onVisible);
      this.disposers.push(() => {
        window.removeEventListener("pageshow", onPageShow);
        window.removeEventListener("online", wake);
        document.removeEventListener("visibilitychange", onVisible);
      });
    }
  }

  /** Stops timers and listeners (tests; an app keeps its session for the page's life). */
  dispose(): void {
    for (const d of this.disposers.splice(0)) d();
    if (this.renewTimer !== null) clearTimeout(this.renewTimer);
    if (this.leaveTimer !== null) clearTimeout(this.leaveTimer);
    this.renewTimer = null;
    this.leaveTimer = null;
  }

  /** True once a sign-out started (the page only waits for its navigation). */
  get isSigningOut(): boolean {
    return this.signingOut !== null;
  }

  get status(): AuthStatus {
    return this.leaving ? "leaving" : this.current;
  }

  private notify() {
    for (const l of this.listeners) l();
  }

  resetLeaving(): void {
    if (this.signingOut) {
      // Never back to a signed-in page once a sign-out started (e.g. restored from the back/forward cache).
      this.tabs.navigate(this.signingOut);
      return;
    }
    if (this.leaveTimer !== null) clearTimeout(this.leaveTimer);
    this.leaveTimer = null;
    if (!this.leaving) return;
    this.leaving = false;
    this.notify();
  }

  private startLeaving(onStuck: () => void) {
    this.leaving = true;
    if (this.leaveTimer !== null) clearTimeout(this.leaveTimer);
    // The navigation normally unloads the page; if it never commits, give the page back.
    this.leaveTimer = setTimeout(() => {
      this.leaveTimer = null;
      if (this.leaving) onStuck();
    }, this.config.leaveTimeoutMs);
    this.notify();
  }

  /** Marks the tab as leaving for Keycloak; a second redirect while one is under way does nothing. */
  private async leave(redirect: () => Promise<void>): Promise<void> {
    if (this.leaving) return;
    this.startLeaving(() => {
      this.resetLeaving();
    });
    try {
      await redirect();
    } catch (e) {
      this.resetLeaving();
      throw e;
    }
  }

  get sessionUser(): SessionUser | null {
    const p = this.user?.profile;
    if (!p) return null;
    return {
      sub: p.sub,
      email: p.email,
      name: p.name,
      acr: typeof p.acr === "string" ? p.acr : undefined,
      authTime: typeof p.auth_time === "number" ? p.auth_time : undefined,
      locale: typeof p.locale === "string" ? p.locale : undefined,
    };
  }

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  private setUser(user: User | null) {
    this.user = user && !user.expired ? user : null;
    this.current = this.user ? "signed-in" : "signed-out";
    this.scheduleRenew();
    this.notify();
  }

  private scheduleRenew() {
    if (this.renewTimer !== null) clearTimeout(this.renewTimer);
    this.renewTimer = null;
    const seconds = this.user?.expires_in;
    if (seconds === undefined) return;
    const target = (seconds - this.config.renewBeforeSeconds) * 1000;
    // Short-lived tokens: renew at half their life, never sooner than MIN_RENEW_DELAY_MS.
    const delay = Math.max(MIN_RENEW_DELAY_MS, target > 0 ? target : (seconds * 1000) / 2);
    this.scheduleRenewIn(delay);
  }

  private scheduleRenewIn(ms: number) {
    if (this.renewTimer !== null) clearTimeout(this.renewTimer);
    this.renewTimer = setTimeout(() => {
      this.renewTimer = null;
      this.renew().catch(() => {
        this.scheduleRenewIn(15_000);
      });
    }, ms);
  }

  private async onTabMessage(data: unknown) {
    const message = data as Partial<TabMessage> | null;
    if (message?.type !== "signed-out" || typeof message.sub !== "string") return;
    const reason: SignOutReason =
      message.reason === "idle" || message.reason === "everywhere" ? message.reason : "user";
    if (this.signingOut || !this.user || this.user.profile.sub !== message.sub) return;
    // Another tab signed out (or timed out): this one shows the same page, without a new login.
    const postLogout = this.beginSignOut(reason, false);
    await this.manager.removeUser().catch(() => undefined);
    this.tabs.navigate(postLogout);
  }

  /**
   * The first step of every sign-out: the tab forgets the person at once (no token, no renew, no sign-in may
   * follow) and is leaving for good. If the navigation never commits, the timer goes to the signed-out page and
   * says Keycloak's session may still be open.
   */
  private beginSignOut(reason: SignOutReason, here: boolean): string {
    const postLogout = `${this.config.origin}/?signedOut=${reason}`;
    this.signingOut = postLogout;
    // The tab's workspace, pending uploads and integration return belong to this person: the next sign-in
    // (perhaps someone else) starts without them, in memory and in the transient store.
    workspace.set(null);
    transientStore.remove("uploads");
    transientStore.remove("integration-return");
    transientStore.remove("onboarding-offered");
    // An export link's one-time token belongs to the person who opened it.
    transientStore.remove("export-token");
    if (this.renewTimer !== null) clearTimeout(this.renewTimer);
    this.renewTimer = null;
    this.user = null;
    this.current = "signed-out";
    this.leaving = true;
    if (this.leaveTimer !== null) clearTimeout(this.leaveTimer);
    this.leaveTimer = setTimeout(() => {
      this.leaveTimer = null;
      this.tabs.navigate(here ? `${postLogout}&unconfirmed=1` : postLogout);
    }, this.config.leaveTimeoutMs);
    this.notify();
    return postLogout;
  }

  isCallback(pathname: string): boolean {
    return pathname === this.config.callbackPath;
  }

  /**
   * True once after the redirect back from a step-up (`completeCallback` of kind `stepup`): only then may a stored
   * step-up action be replayed.
   */
  takeStepUpReturn(): boolean {
    const value = this.stepUpReturn;
    this.stepUpReturn = false;
    return value;
  }

  /**
   * Completes the redirect back from Keycloak. `signingOut`: a sign-out is under way, nobody was signed in (the
   * caller does nothing; the sign-out's navigation follows). Returns where to go. A `prompt=none` round trip that found no
   * Keycloak session answers `login_required`: then the interactive login starts (`interactive: true`).
   */
  async completeCallback(url: string): Promise<CallbackResult> {
    // A sign-out under way wins: the code is not even exchanged, nobody is signed in.
    if (this.signingOut) return { returnTo: "/", signingOut: true };
    try {
      const user = await this.manager.signinRedirectCallback(url);
      if (this.signingOut) {
        // The sign-out started while the code was exchanged (e.g. announced by another tab): drop the new tokens.
        await this.manager.revokeTokens(["refresh_token"]).catch(() => undefined);
        await this.manager.removeUser().catch(() => undefined);
        return { returnTo: "/", signingOut: true };
      }
      const state = (user.state ?? {}) as Partial<LoginState>;
      const acr = user.profile.acr;
      if (typeof acr !== "string" || !this.config.acceptedAcr.includes(acr)) {
        // Right after sign-up or a password reset Keycloak issues a password-level token (KEYCLOAK-THEME §1):
        // revoke and drop it, and sign in with the second factor (or its forced enrolment), without an error.
        await this.manager.revokeTokens(["refresh_token"]).catch(() => undefined);
        await this.manager.removeUser();
        if (state.kind === "login-mfa") throw new Error(`the login returned acr=${String(acr)} twice`);
        return { returnTo: safeReturnTo(state.returnTo), interactive: true, kind: "login-mfa" };
      }
      // Only a real step-up (Keycloak's level 3) allows the stored action to be replayed.
      this.stepUpReturn = state.kind === "stepup" && acr === "stepup";
      this.setUser(user);
      return { returnTo: safeReturnTo(state.returnTo), kind: state.kind };
    } catch (e) {
      const error = (e as { error?: string }).error;
      // oidc-client-ts's ErrorResponse carries our custom state as `state`.
      const state = (e as { state?: Partial<LoginState> }).state;
      if (error === "login_required" || error === "interaction_required" || error === "consent_required") {
        return { returnTo: safeReturnTo(state?.returnTo), interactive: true };
      }
      throw e;
    }
  }

  /** Signs in: `silent` first tries Keycloak's SSO session without showing a form (after a reload). */
  async signIn(
    returnTo: string,
    {
      silent = false,
      kind = "login",
      fresh = false,
      create = false,
    }: {
      silent?: boolean;
      kind?: "login" | "login-mfa";
      /** `prompt=login`: never the old SSO session. */ fresh?: boolean;
      /** `prompt=create`: Keycloak's registration form (the site's "Start free trial"). */ create?: boolean;
    } = {},
  ): Promise<void> {
    if (this.signingOut) return;
    const state: LoginState = { returnTo: safeReturnTo(returnTo), kind };
    const prompt = silent ? "none" : create ? "create" : fresh ? "login" : undefined;
    await this.leave(() =>
      this.manager.signinRedirect({
        state,
        acr_values: this.config.loginAcr,
        // Keycloak's pages in the app's language (L1; the theme falls back to English for a locale it lacks).
        ui_locales: activeLocale(),
        ...(prompt ? { prompt } : {}),
      }),
    );
  }

  /** 401 `mfa-required` from the API: one redirect per page for the second factor, never a parallel one. */
  async requireSecondFactor(returnTo: string): Promise<void> {
    await this.signIn(returnTo, { kind: "login-mfa" });
  }

  /**
   * Step-up (11 §2.4): a fresh OTP or WebAuthn, `acr=stepup`. Keycloak's level 3 has max age 0, so the factor is
   * always asked again; `max_age` is the API's challenge (RFC 9470, `max_age="600"`): within 10 minutes of the
   * sign-in only the factor is asked. `max_age=0` (until F14 live) made Keycloak start over: password, the level-2
   * factor and the level-3 factor again (REQUESTS #106).
   */
  async stepUp(returnTo: string): Promise<void> {
    const state: LoginState = { returnTo: safeReturnTo(returnTo), kind: "stepup" };
    await this.leave(() =>
      this.manager.signinRedirect({
        state,
        acr_values: "stepup",
        max_age: STEP_UP_MAX_AGE_SECONDS,
        ui_locales: activeLocale(),
      }),
    );
  }

  /** An application-initiated action, e.g. `webauthn-register` ("Add a passkey", KEYCLOAK-THEME §2.11). */
  async requiredAction(action: "webauthn-register", returnTo: string): Promise<void> {
    const state: LoginState = { returnTo: safeReturnTo(returnTo), kind: "action" };
    await this.leave(() =>
      this.manager.signinRedirect({ state, ui_locales: activeLocale(), extraQueryParams: { kc_action: action } }),
    );
  }

  /** The one refresh: concurrent callers share the in-flight promise (Keycloak spends a refresh token on use). */
  renew(): Promise<User | null> {
    const before = this.user;
    if (!before) return Promise.resolve(null);
    this.inflight ??= this.refreshWithRetry(before).finally(() => {
      this.inflight = null;
    });
    return this.inflight;
  }

  private async refreshWithRetry(before: User): Promise<User | null> {
    for (let attempt = 0; ; attempt++) {
      try {
        const renewed = await this.manager.signinSilent();
        if (!renewed) throw new Error("no tokens");
        // A sign-out (or a new sign-in) started meanwhile: this result belongs to nobody.
        if (this.signingOut || this.user !== before) return this.user;
        this.setUser(renewed);
        return renewed;
      } catch (e) {
        if (this.signingOut || this.user !== before) return this.user; // signed out (or in) meanwhile
        if (isTerminalOAuthError(e)) {
          // Revoked, reused or expired (idle timeout, sign-out elsewhere), or refused: the session is over.
          await this.manager.removeUser();
          this.setUser(null);
          return null;
        }
        const delay = this.config.retryDelaysMs[attempt];
        if (delay === undefined) {
          // Still offline or Keycloak down: keep the user and try again; an expired token makes the session
          // unavailable (requests would fail) until a refresh works.
          if (before.expired) {
            this.current = "unavailable";
            this.notify();
          }
          this.scheduleRenewIn(15_000);
          return before;
        }
        await sleep(delay);
      }
    }
  }

  /** The access token for the API; renews it first (shared) when it expires soon. */
  getAccessToken = async (): Promise<string | null> => {
    const user = this.user;
    if (!user) return null;
    if ((user.expires_in ?? 0) > 30) return user.access_token;
    const renewed = await this.renew();
    return renewed && !renewed.expired ? renewed.access_token : null;
  };

  /** "Stay signed in": true only if Keycloak issued new tokens (its SSO session was extended). */
  async refresh(): Promise<boolean> {
    const before = this.user;
    if (!before) return false;
    const after = await this.renew();
    return after !== null && after !== before;
  }

  /**
   * Ends the Keycloak session (RP-initiated logout; every app of this browser session is signed out) and comes
   * back to `/?signedOut=<reason>`, which shows "Your session ended" instead of an automatic new login.
   *
   * The tab is `leaving` from the first moment, so nothing signs in again meanwhile. If oidc-client-ts fails
   * (e.g. the revocation call), the user is dropped anyway and the browser goes to the end-session endpoint
   * directly; if even that is unknown (Keycloak unreachable), to the local signed-out page (Keycloak's own session
   * then ends at its idle timeout: no refresh keeps it alive).
   */
  async signOut(reason: SignOutReason = "user"): Promise<void> {
    if (this.signingOut) return;
    const user = this.user;
    const idToken = user?.id_token;
    const sub = user?.profile.sub;
    const postLogout = this.beginSignOut(reason, true);
    if (sub) this.tabs.channel?.postMessage({ type: "signed-out", sub, reason } satisfies TabMessage);
    try {
      await this.manager.signoutRedirect({ id_token_hint: idToken, post_logout_redirect_uri: postLogout });
      return;
    } catch {
      // Fall through: drop the tokens and end the session by hand.
    }
    await this.manager.removeUser().catch(() => undefined);
    const endpoint = await this.manager.metadataService.getEndSessionEndpoint().catch(() => undefined);
    if (!endpoint) {
      // Keycloak unreachable: its session may still be open (it ends at its idle timeout; nothing refreshes it).
      this.tabs.navigate(`${postLogout}&unconfirmed=1`);
      return;
    }
    const url = new URL(endpoint);
    if (idToken) url.searchParams.set("id_token_hint", idToken);
    url.searchParams.set("client_id", this.config.clientId);
    url.searchParams.set("post_logout_redirect_uri", postLogout);
    this.tabs.navigate(url.toString());
  }
}
